import { supabase } from '@/lib/supabase'
import { fetchMaps, enrichItems } from './helpers'
import type { TagRef, CategoryRef, ProfileInfo } from './helpers'
import type { Scope } from '@/lib/db-types'
import type { ScopeFilter } from '@/lib/utils'

export type TransactionItem = {
  id: string
  name: string
  amount: number
  category_id: string | null
  category_name: string | null
  parent_category_name: string | null
  parent_category_color: string | null
}

export type Transaction = {
  id: string
  date: string
  store_name: string | null
  memo: string | null
  total_amount: number
  tax_amount: number
  input_method: string
  recurring_transaction_id?: string | null
  paid_by: string
  paid_by_name: string | null
  paid_by_avatar_url: string | null
  scope: 'household' | 'personal'
  items: TransactionItem[]
  tags: TagRef[]
  created_at: string
}

export function groupBy<T>(arr: T[], key: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>()
  for (const item of arr) {
    const k = key(item)
    const group = map.get(k)
    if (group) {
      group.push(item)
    } else {
      map.set(k, [item])
    }
  }
  return map
}

export function mapTransaction(
  tx: { id: string; date: string; store_name: string | null; memo: string | null; total_amount: number; tax_amount?: number; input_method: string; recurring_transaction_id: string | null; paid_by: string; scope: string; created_at: string | null },
  itemsForTx: Array<{ id: string; name: string; amount: number; category_id: string | null }>,
  tagsForTx: Array<{ tag_id: string }>,
  profileMap: Map<string, ProfileInfo>,
  categoryMap: Map<string, CategoryRef>,
  tagMap: Map<string, TagRef>,
): Transaction {
  return {
    id: tx.id,
    date: tx.date,
    store_name: tx.store_name,
    memo: tx.memo ?? null,
    total_amount: tx.total_amount,
    tax_amount: tx.tax_amount ?? 0,
    input_method: tx.input_method,
    recurring_transaction_id: tx.recurring_transaction_id,
    paid_by: tx.paid_by,
    paid_by_name: profileMap.get(tx.paid_by)?.display_name ?? null,
    paid_by_avatar_url: profileMap.get(tx.paid_by)?.avatar_url ?? null,
    scope: tx.scope as Scope,
    items: enrichItems(itemsForTx, categoryMap),
    tags: tagsForTx
      .map(tt => tagMap.get(tt.tag_id))
      .filter((t): t is TagRef => t != null),
    created_at: tx.created_at!,
  }
}

export async function fetchTransactions(month: string, scope: ScopeFilter = 'all'): Promise<Transaction[]> {
  const [year, mon] = month.split('-').map(Number)
  const startDate = `${month}-01`
  const nextMonth = mon === 12
    ? `${year + 1}-01-01`
    : `${year}-${String(mon + 1).padStart(2, '0')}-01`

  let query = supabase
    .from('transactions')
    .select('*')
    .gte('date', startDate)
    .lt('date', nextMonth)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false })

  if (scope !== 'all') {
    query = query.eq('scope', scope)
  }

  const { data: transactions, error } = await query

  if (error) throw new Error(error.message)
  if (!transactions?.length) return []

  const txIds = transactions.map(t => t.id)
  const [{ data: items }, { data: txTags }, { profileMap, categoryMap, tagMap }] = await Promise.all([
    supabase.from('transaction_items').select('*').in('transaction_id', txIds),
    supabase.from('transaction_tags').select('transaction_id, tag_id').in('transaction_id', txIds),
    fetchMaps(),
  ])

  const itemsByTxId = groupBy(items ?? [], item => item.transaction_id)
  const tagsByTxId = groupBy(txTags ?? [], tt => tt.transaction_id)

  return transactions.map(tx =>
    mapTransaction(tx, itemsByTxId.get(tx.id) ?? [], tagsByTxId.get(tx.id) ?? [], profileMap, categoryMap, tagMap)
  )
}

export async function fetchTransaction(id: string): Promise<Transaction | null> {
  const [{ data: tx }, { profileMap, categoryMap, tagMap }] = await Promise.all([
    supabase.from('transactions').select('*').eq('id', id).single(),
    fetchMaps(),
  ])

  if (!tx) return null

  const [{ data: items }, { data: txTags }] = await Promise.all([
    supabase.from('transaction_items').select('*').eq('transaction_id', id),
    supabase.from('transaction_tags').select('tag_id').eq('transaction_id', id),
  ])

  return mapTransaction(tx, items ?? [], txTags ?? [], profileMap, categoryMap, tagMap)
}

export async function createTransaction(data: {
  paid_by: string
  date: string
  store_name?: string
  memo?: string
  total_amount: number
  tax_amount?: number
  input_method: string
  items: Array<{ name: string; amount: number; category_id?: string | null }>
  scope?: 'household' | 'personal'
  tag_ids?: string[]
}) {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Unauthorized')

  const { data: txId, error } = await supabase.rpc('create_transaction', {
    p_user_id: user.id,
    p_paid_by: data.paid_by,
    p_date: data.date,
    p_store_name: (data.store_name || null) as string,
    p_total_amount: data.total_amount,
    p_input_method: data.input_method,
    p_items: data.items,
    p_scope: data.scope || 'household',
    p_memo: (data.memo || null) as string,
    p_tag_ids: data.tag_ids?.length ? data.tag_ids : null,
    p_tax_amount: data.tax_amount ?? 0,
  })

  if (error) throw new Error(error.message)
  return txId!
}

export async function updateTransaction(
  id: string,
  data: {
    paid_by: string
    date: string
    store_name?: string
    memo?: string | null
    total_amount: number
    tax_amount?: number
    items: Array<{ name: string; amount: number; category_id?: string | null }>
    scope?: 'household' | 'personal'
    tag_ids?: string[]
  },
) {
  const { error } = await supabase.rpc('update_transaction', {
    p_id: id,
    p_paid_by: data.paid_by,
    p_date: data.date,
    p_store_name: (data.store_name || null) as string,
    p_total_amount: data.total_amount,
    p_items: data.items,
    p_scope: (data.scope || null) as string,
    p_memo: (data.memo ?? null) as string,
    p_tag_ids: data.tag_ids ?? null,
    p_tax_amount: data.tax_amount ?? 0,
  })

  if (error) throw new Error(error.message)
}

export async function fetchTransactionsByTag(tagId: string, scope: ScopeFilter = 'all'): Promise<Transaction[]> {
  let query = supabase
    .from('transactions')
    .select('*, transaction_tags!inner(tag_id)')
    .eq('transaction_tags.tag_id', tagId)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false })

  if (scope !== 'all') {
    query = query.eq('scope', scope)
  }

  const { data: transactions, error: txError } = await query

  if (txError) throw new Error(txError.message)
  if (!transactions?.length) return []

  const txIds = transactions.map(t => t.id)

  const [{ data: items }, { data: allTxTags }, { profileMap, categoryMap, tagMap }] = await Promise.all([
    supabase.from('transaction_items').select('*').in('transaction_id', txIds),
    supabase.from('transaction_tags').select('transaction_id, tag_id').in('transaction_id', txIds),
    fetchMaps(),
  ])

  const itemsByTxId = groupBy(items ?? [], item => item.transaction_id)
  const tagsByTxId = groupBy(allTxTags ?? [], tt => tt.transaction_id)

  return transactions.map(tx =>
    mapTransaction(tx, itemsByTxId.get(tx.id) ?? [], tagsByTxId.get(tx.id) ?? [], profileMap, categoryMap, tagMap)
  )
}

export async function deleteTransaction(id: string) {
  const { error } = await supabase
    .from('transactions')
    .delete()
    .eq('id', id)

  if (error) throw new Error(error.message)
}
